import React, { useEffect, useState, useCallback } from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import debounce from 'lodash/debounce';
import appSettings from '../../config/settings';
import { types as sdkTypes } from '../../util/sdkLoader';
import sdk from '../../util/sdkLoader';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { composeValidators, moneySubUnitAmountAtLeast, numberAtLeast, required } from '../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../transactions/transaction';

import {
  Form,
  FieldTextInput,
  PrimaryButton,
  H6,
  FieldCurrencyInput,
  FieldSelect,
} from '../../components';

import EstimatedCustomerBreakdownMaybe from './EstimatedCustomerBreakdownMaybe';

import css from './OrderPanel.module.css';
import { formatMoney } from '../../util/currency';
const { Money } = sdkTypes;

const handleFetchLineItems = ({
  quantity,
  deliveryMethod,
  price,
  displayDeliveryMethod,
  listingId,
  originalListingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
}) => {
  const stockReservationQuantity = Number.parseInt(quantity, 10);
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const isBrowser = typeof window !== 'undefined';
  if (
    isBrowser &&
    stockReservationQuantity &&
    (!displayDeliveryMethod || deliveryMethod) &&
    !fetchLineItemsInProgress
  ) {
    onFetchTransactionLineItems({
      orderData: { stockReservationQuantity, ...deliveryMethodMaybe, price },
      listingId,
      isOwnListing,
      originalListingId,
    });
  }
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);
  const {
    handleSubmit,
    form: formApi,
    intl,
    formId,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    marketplaceName,
    values,
    marketplaceCurrency,
    currentUserIsProvider,
    quantity,
    ownListings = [],
    listing,
    currentUser,
  } = formRenderProps;

  // ⬇️ Si el anuncio que estoy viendo es de recruitment/staffing
  const isStaffingListing =
    //listing?.attributes?.publicData?.service === 'staffing' ||
    listing?.attributes?.publicData?.service === 'recruitment';

  useEffect(() => {
    setMounted(true);

    const { quantity, deliveryMethod } = values;
    const priceMaybe = { price: new Money(0, marketplaceCurrency) };
    const effectiveQuantity = isStaffingListing ? 160 : (formRenderProps.quantity || quantity);

    if ((effectiveQuantity || formRenderProps.quantity) && !formRenderProps.hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        quantity: effectiveQuantity,
        ...priceMaybe,
        deliveryMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  }, [isStaffingListing]);

  useEffect(() => {
    if (currentUser && listingId) {
      sdk.transactions
        .query({
          customer_id: currentUser.id.uuid,
          listing_id: listingId,
          per_page: 1,
        })
        .then(res => {
          if (res.data.data.length > 0) {
            setAlreadyRequested(true);
          } else {
            setAlreadyRequested(false);
          }
        })
        .catch(() => setAlreadyRequested(false));
    }
  }, [currentUser, listingId]);

  const handleOnChange = useCallback(
    debounce(formValues => {
      const { quantity, listing, deliveryMethod } = formValues.values;
      if (mounted && listing) {
        const effectiveQuantity = isStaffingListing ? 160 : quantity;
        handleFetchLineItems({
          quantity: effectiveQuantity,
          deliveryMethod,
          price,
          listingId: listing,
          originalListingId: listingId,
          isOwnListing,
          fetchLineItemsInProgress,
          onFetchTransactionLineItems,
        });
      }
    }, 300),
    [mounted, listingId, isOwnListing, fetchLineItemsInProgress, onFetchTransactionLineItems, isStaffingListing, ownListings, marketplaceCurrency]
  );

  const handleFormSubmit = e => {
    const { quantity, listing, deliveryMethod } = values || {};
    if (!isStaffingListing && (!quantity || quantity < 1)) {
      e.preventDefault();
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (ownListings && ownListings.length > 1 && !listing) {
      e.preventDefault();
      formApi.blur('listing');
      formApi.focus('listing');
    } else if (displayDeliveryMethod && !deliveryMethod) {
      e.preventDefault();
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const showBreakdown =
    lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

  const quantityRequiredMsg = intl.formatMessage({ id: 'EstimationForm.quantityRequired' });

  const submitInProgress = fetchLineItemsInProgress;

  const submitDisabled = submitInProgress ||
    (!isStaffingListing && !values?.quantity) ||
    !values.listing;

  if (alreadyRequested) {
    return (
      <div style={{
        background: '#f0f4ff',
        color: '#2a3b8f',
        border: '1px solid #b3c6ff',
        borderRadius: '8px',
        padding: '24px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        margin: '32px 0'
      }}>
        Ya solicitaste esta oportunidad.
      </div>
    );
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />
      {currentUserIsProvider ? (
        <>
          {!isStaffingListing && (
            <FieldTextInput
              id={`${formId}.quantity`}
              className={css.quantityField}
              name="quantity"
              type="number"
              min={1}
              label={intl.formatMessage({ id: 'TransactionPanel.hourEstimate' })}
              placeholder={intl.formatMessage({ id: 'TransactionPanel.hourEstimatePlaceholder' })}
              validate={numberAtLeast(quantityRequiredMsg, 1)}
            />
          )}

          <FieldSelect
            id={`${formId}.listing`}
            className={css.input}
            name="listing"
            label="Selecciona un perfil"
            validate={required('Porfavor, selecciona un perfil')}
          >
            <option>Selecciona un perfil profesional</option>
            {(ownListings || []).map(l => {
              // ⬇️ Si el anuncio que estoy solicitando es RECRUITMENT/STAFFING, NO muestro precio en las opciones
              const formattedPrice = l?.attributes?.price ? formatMoney(intl, l?.attributes?.price) : 'USD 0.00';
              const onlyTitle = l?.attributes?.title || '';
              const optionText = isStaffingListing
                ? onlyTitle
                : `${onlyTitle} - ${formattedPrice}`;
              return (
                <option key={l.id.uuid} value={l.id.uuid}>
                  {optionText}
                </option>
              );
            })}
          </FieldSelect>

          {!price?.amount && false && (
            <FieldCurrencyInput
              id="price"
              name="price"
              className={css.input}
              label={"Precio por hora"}
              placeholder="Precio por hora"
              currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
              validate={composeValidators(
                required('Precio por hora es requerido'),
                moneySubUnitAmountAtLeast('El precio por hora debe ser mayor que 5 USD', 500)
              )}
            />
          )}
        </>
      ) : null}

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="EstimationForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={lineItems}
            lineItems={lineItems}
            currency={price?.currency || 'USD'}
            marketplaceName={marketplaceName}
            processName={PURCHASE_PROCESS_NAME}
          />
        </div>
      ) : null}

      {currentUserIsProvider ? (
        <div className={css.submitButton}>
          <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
            <FormattedMessage id="EstimationForm.ctaButton" />
          </PrimaryButton>
        </div>
      ) : null}
    </Form>
  );
};

const InitialEstimationForm = props => {
  const intl = useIntl();
  const {
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    displayDeliveryMethod,
    allowOrdersOfMultipleItems,
    ownListings,
  } = props;

  if (displayDeliveryMethod && !pickupEnabled && !shippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="EstimationForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock && currentStock === 1;
  const hasOneItemMode = !allowOrdersOfMultipleItems && currentStock > 0;
  const quantityMaybe = hasOneItemLeft || hasOneItemMode ? { quantity: '1' } : {};
  const deliveryMethodMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
        ? { deliveryMethod: 'pickup' }
        : !shippingEnabled && !pickupEnabled
          ? { deliveryMethod: 'none' }
          : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;

  let listingMaybe = {};
  if (ownListings.length == 1) {
    listingMaybe = { listing: ownListings[0].id.uuid };
  }

  const initialValues = { ...quantityMaybe, ...deliveryMethodMaybe, ...listingMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      displayDeliveryMethod={displayDeliveryMethod}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

InitialEstimationForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  pickupEnabled: false,
  shippingEnabled: false,
  displayDeliveryMethod: false,
  lineItems: null,
  fetchLineItemsError: null,
};

InitialEstimationForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  formId: string.isRequired,
  onSubmit: func.isRequired,

  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,
  pickupEnabled: bool,
  shippingEnabled: bool,
  displayDeliveryMethod: bool,

  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

export default InitialEstimationForm;
